import { useContext, useEffect, useReducer, useRef } from "react";
import {
  ComparePkgTrayContext,
  ComparePkgTrayContextValue,
  ComparePkgTrayStore
} from "./ComparePkgTrayContext";

// Shallow field equality — used by the full-snapshot consumers so a fresh snapshot
// object with identical field values does NOT trigger a re-render. getSnapshot()
// returns a new object each call (to stay config-fresh), so plain Object.is would
// always report "changed"; shallow compare restores the "only re-render on real
// change" behaviour without caching (which would risk stale CMS config).
function shallowEqual(a: any, b: any): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (typeof a !== "object" || a === null || typeof b !== "object" || b === null) {
    return false;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every(key => Object.is(a[key], b[key]));
}

/**
 * Subscribe to a slice of the compare store and re-render only when that slice
 * changes (per the `isEqual` comparator). A per-card CompareBox selects its own
 * `selectedItems.includes(id)` boolean, so toggling one card no longer re-renders
 * every other card — this is what removes the slow compare-toggle re-render cascade.
 *
 * The slice is computed FRESH on every render (so a changed `tag`/cardId prop is
 * reflected immediately), and a store subscription forces a re-render only when the
 * selected value actually changes. This is the manual equivalent of a selector-aware
 * store. We deliberately avoid `useSyncExternalStore` because it throws under enzyme
 * shallow rendering; `useEffect` is simply skipped there, so the component still
 * renders its initial slice. Behaviour is identical in real DOM and SSR (the slice
 * is derived from the store's initial state on the server, with no live subscription).
 */
function useStoreSlice<T>(
  store: ComparePkgTrayStore | undefined,
  select: (store: ComparePkgTrayStore) => T,
  fallback: T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  const [, forceRender] = useReducer((c: number) => c + 1, 0);
  const current = store ? select(store) : fallback;
  // Refs hold the latest rendered value and selector so the subscription closure
  // (registered once per store) always compares against current data.
  const valueRef = useRef(current);
  const selectRef = useRef(select);
  const isEqualRef = useRef(isEqual);
  valueRef.current = current;
  selectRef.current = select;
  isEqualRef.current = isEqual;

  useEffect(() => {
    if (!store) {
      return undefined;
    }
    const check = () => {
      const next = selectRef.current(store);
      if (!isEqualRef.current(valueRef.current, next)) {
        valueRef.current = next;
        forceRender();
      }
    };
    // Re-check once in case the store changed between render and effect commit.
    check();
    return store.subscribe(check);
  }, [store]);

  return current;
}

// Full flat snapshot — for single-instance tray consumers that need most fields.
// Re-renders whenever any compare state changes (same semantics as before).
export function useComparePkgTrayContext(): ComparePkgTrayContextValue {
  const store = useContext(ComparePkgTrayContext);
  if (!store) {
    throw new Error("useComparePkgTrayContext must be used within a ComparePkgTrayProvider");
  }
  // INVARIANT: `store` is non-null past the throw above, so useStoreSlice always takes
  // its `store ? select(store) : fallback` → select branch and the fallback is dead.
  // We therefore pass a non-allocating placeholder rather than build a throwaway
  // snapshot every render. NOTE: this cast is only sound because of the throw — if the
  // throw is ever removed, restore `store.getSnapshot()` so `undefined` can't escape.
  return useStoreSlice(store, s => s.getSnapshot(), undefined as unknown as ComparePkgTrayContextValue, shallowEqual);
}

// Generated by Copilot- Start - 2026-04-10 16:47:32
/**
 * Returns the context value when inside a ComparePkgTrayProvider,
 * or undefined when rendered outside one.
 * Use this instead of wrapping useComparePkgTrayContext in try/catch,
 * which violates React's rules of hooks.
 */
export function useOptionalComparePkgTrayContext(): ComparePkgTrayContextValue | undefined {
  const store = useContext(ComparePkgTrayContext);
  return useStoreSlice(store, s => s.getSnapshot(), undefined, shallowEqual);
}
// Generated by Copilot- End - 2026-04-10 16:47:32

/**
 * Narrow selector: subscribes ONLY to whether `tag` is currently selected.
 * Returns a boolean, so the caller re-renders only when THAT tag's membership
 * flips — not on every other card's selection. Returns false outside a provider.
 */
export function useIsCompareSelected(tag: any): boolean {
  const store = useContext(ComparePkgTrayContext);
  return useStoreSlice(store, s => s.getState().selectedItems.includes(tag), false);
}

/**
 * Returns the store handle for imperative actions (updateSelectedTag, setTrayOpen,
 * updateCollapsedTray) and latest-state reads via getState() inside event handlers.
 * The store identity is created once per provider and never changes, so reading it
 * here does not subscribe to state and never triggers a re-render. Returns undefined
 * outside a provider.
 */
export function useComparePkgTrayStore(): ComparePkgTrayStore | undefined {
  return useContext(ComparePkgTrayContext);
}
